import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, Zap, Shield, Bolt, Sparkles } from 'lucide-react'
import { storeService } from '../services/storeService'
import useToastStore from '../store/useToastStore'
import { useAnalytics } from '../hooks/useAnalytics'
import { AnalyticsEvents } from '../utils/analytics'
import useEntity from '../hooks/useEntity'
import { setMetaUser, trackMetaStandard } from '../config/metaPixel'

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isUrlValid, setIsUrlValid] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const toast = useToastStore();
  const { track, setUserProperties } = useAnalytics();
  const { data: entity } = useEntity();

  // Validation function for brand name
  const validateBrandName = (name: string) => {
    if (!name.trim()) {
      setNameError('');
      setIsNameValid(false);
      return;
    }

    // Length validation
    if (name.length < 2) {
      setNameError('Brand name must be at least 2 characters long');
      setIsNameValid(false);
      return;
    }

    if (name.length > 50) {
      setNameError('Brand name must be less than 50 characters');
      setIsNameValid(false);
      return;
    }

    // Special character validation - allow letters, numbers, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z0-9\s\-']+$/;
    if (!nameRegex.test(name)) {
      setNameError('Brand name can only contain letters, numbers, spaces, hyphens, and apostrophes');
      setIsNameValid(false);
      return;
    }

    // Check for consecutive spaces
    if (name.includes('  ')) {
      setNameError('Brand name cannot contain consecutive spaces');
      setIsNameValid(false);
      return;
    }

    // Check if name starts or ends with space
    if (name.startsWith(' ') || name.endsWith(' ')) {
      setNameError('Brand name cannot start or end with a space');
      setIsNameValid(false);
      return;
    }

    setNameError('');
    setIsNameValid(true);
  };

  // Validation function for subdomain
  const validateSubdomain = (url: string) => {
    if (!url.trim()) {
      setUrlError('');
      setIsUrlValid(false);
      return;
    }

    // Subdomain validation rules
    const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

    if (url.length < 3) {
      setUrlError('Subdomain must be at least 3 characters long');
      setIsUrlValid(false);
      return;
    }

    if (url.length > 63) {
      setUrlError('Subdomain must be less than 63 characters');
      setIsUrlValid(false);
      return;
    }

    if (!subdomainRegex.test(url)) {
      setUrlError('Subdomain can only contain lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen.');
      setIsUrlValid(false);
      return;
    }

    // Check for consecutive hyphens
    if (url.includes('--')) {
      setUrlError('Subdomain cannot contain consecutive hyphens');
      setIsUrlValid(false);
      return;
    }

    // Reserved subdomain check (you can add more as needed)
    const reservedSubdomains = ['www', 'api', 'admin', 'mail', 'ftp', 'blog', 'shop', 'store', 'app'];
    if (reservedSubdomains.includes(url.toLowerCase())) {
      setUrlError('This subdomain is reserved and cannot be used');
      setIsUrlValid(false);
      return;
    }

    setUrlError('');
    setIsUrlValid(true);
  };

  // Handle brand name input change with validation
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStoreName(value);
    validateBrandName(value);
  };

  // Handle URL input change with validation
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setStoreUrl(value);
    validateSubdomain(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeName.trim() || !storeUrl.trim()) {
      toast.showToast('Please fill in all fields', 'error');
      return;
    }

    if (!isNameValid) {
      toast.showToast('Please enter a valid brand name', 'error');
      return;
    }

    if (!isUrlValid) {
      toast.showToast('Please enter a valid subdomain', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await storeService.createStore({ brandName: storeName, url: storeUrl });
      setUserProperties({
        store_name: storeName,
        store_url: storeUrl,
        onboarding_completed_at: new Date().toISOString(),
      });
      track({
        name: AnalyticsEvents.STORE_CREATED,
        properties: {
          store_name: storeName,
          store_url: storeUrl,
        },
      });

      // Meta pixel — merchant acquisition funnel: store created. eventID matches
      // the backend CAPI id (store_${entityId}) for deduplication.
      if (entity?.entityId != null) {
        setMetaUser({ phone: entity.mobile, email: entity.email, externalId: entity.entityId });
        trackMetaStandard('StartTrial', `store_${entity.entityId}`, {
          content_name: storeUrl,
        });
      }
      toast.showToast('Store created successfully!', 'success');

      // Store onboarding completion flag
      localStorage.setItem('onboarding_completed', 'true');

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating store:', error);
      // Show the specific error message from the backend if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to create store. Please try again.';
      toast.showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 py-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="max-w-xl w-full relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 shadow-[0_0_30px_rgba(99,102,241,0.3)]">
            <Store className="w-10 h-10 md:w-12 md:h-12 text-white" />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-blue-600 inline-block text-transparent bg-clip-text">
            Create Your Store
          </h1>
          <p className="text-gray-600 text-base md:text-xl">Launch your digital storefront instantly</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-purple-100 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
            <div>
              <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-2">
                Store Name
              </label>
              <input
                type="text"
                id="storeName"
                value={storeName}
                onChange={handleNameChange}
                placeholder="Enter your store name"
                className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 ${nameError
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                  : isNameValid
                    ? 'border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                    : 'border-gray-200 focus:border-purple-200 focus:ring-2 focus:ring-purple-100'
                  }`}
                disabled={isLoading}
              />
              {nameError && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {nameError}
                </p>
              )}
              {isNameValid && storeName && (
                <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Valid brand name
                </p>
              )}
            </div>

            <div>
              <label htmlFor="storeUrl" className="block text-sm font-medium text-gray-700 mb-2">
                Store URL
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="storeUrl"
                  value={storeUrl}
                  onChange={handleUrlChange}
                  placeholder="your-store"
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 pr-32 ${urlError
                    ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                    : isUrlValid
                      ? 'border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                      : 'border-gray-200 focus:border-purple-200 focus:ring-2 focus:ring-purple-100'
                    }`}
                  disabled={isLoading}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                  .mart-24.com
                </span>
              </div>
              {urlError && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {urlError}
                </p>
              )}
              {isUrlValid && storeUrl && (
                <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Valid subdomain
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : null}
              <span className="font-medium">
                {isLoading ? 'Creating Store...' : 'Create Store'}
              </span>
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}